<template>
  <div class="app-container">
    <Transition name="update-fade">
      <div
        v-if="isUpdating"
        class="fixed inset-0 z-[10000] flex items-center justify-center bg-white dark:bg-gray-950"
      >
        <div class="flex flex-col items-center gap-8">
          <div class="flex flex-col items-center gap-3">
            <div class="relative">
              <div class="tb-ci-update-icon-wrap flex h-32 w-32 items-center justify-center rounded-full">
                <img
                  v-if="logoUrl"
                  :src="logoUrl"
                  :alt="logoAlt"
                  class="h-20 w-20 object-contain"
                >
                <svg v-else class="tb-ci-text-accent h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="1.75"
                    d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                  />
                </svg>
              </div>
              <div class="absolute inset-0 animate-spin rounded-full border-4 border-[rgb(var(--tenant-brand-500)/1)] border-t-transparent" />
              <div
                class="absolute inset-[-8px] animate-spin rounded-full border-4 border-[rgb(var(--tenant-brand-500)/0.5)] border-r-transparent"
                style="animation-duration: 1.5s; animation-direction: reverse"
              />
              <div
                class="absolute inset-[-16px] animate-spin rounded-full border-4 border-[rgb(var(--tenant-brand-500)/0.3)] border-b-transparent"
                style="animation-duration: 2s"
              />
            </div>
            <p class="text-sm font-semibold tracking-wide text-gray-500 dark:text-gray-400" :style="headingStyle">
              {{ displayName || t('app.name') }}
            </p>
          </div>

          <div class="flex min-h-[120px] w-full flex-col items-center gap-4">
            <Transition name="phase-fade" mode="out-in">
              <div :key="updatePhase" class="flex flex-col items-center">
                <p class="mb-2 text-2xl font-bold text-gray-900 dark:text-white">
                  {{ updatePhaseText }}
                </p>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ updatePhaseSubtext }}
                </p>
              </div>
            </Transition>

            <div class="mt-4 flex w-64 flex-col items-center gap-2">
              <div class="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  class="h-full rounded-full bg-[rgb(var(--tenant-brand-600)/1)] transition-all duration-500 ease-out"
                  :style="{ width: `${updateProgress}%` }"
                />
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400">{{ updateProgress }}%</p>
            </div>
          </div>

          <div class="flex gap-2">
            <span class="h-3 w-3 animate-bounce rounded-full bg-[rgb(var(--tenant-brand-500)/1)]" style="animation-delay: 0s" />
            <span class="h-3 w-3 animate-bounce rounded-full bg-[rgb(var(--tenant-brand-500)/1)]" style="animation-delay: 0.2s" />
            <span class="h-3 w-3 animate-bounce rounded-full bg-[rgb(var(--tenant-brand-500)/1)]" style="animation-delay: 0.4s" />
          </div>
        </div>
      </div>
    </Transition>

    <!-- Initial bootstrap loader -->
    <Transition name="loader-fade">
      <div
        v-if="isInitializing && !isUpdating"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-white dark:bg-gray-950"
      >
        <div class="flex flex-col items-center gap-6">
          <div class="relative">
            <div class="tb-ci-splash-icon-wrap flex h-20 w-20 items-center justify-center">
              <svg class="tb-ci-text-accent h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.75"
                  d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                />
              </svg>
            </div>
            <div class="absolute inset-0 animate-spin rounded-2xl tb-ci-loader-spin" />
          </div>
          <div class="flex gap-1.5">
            <span class="tb-ci-dot h-2 w-2 animate-bounce rounded-full" style="animation-delay: 0s" />
            <span class="tb-ci-dot h-2 w-2 animate-bounce rounded-full" style="animation-delay: 0.2s" />
            <span class="tb-ci-dot h-2 w-2 animate-bounce rounded-full" style="animation-delay: 0.4s" />
          </div>
        </div>
      </div>
    </Transition>

    <!-- Shell: PCG pcg-frontend — auth overlays are siblings of .app-content (not under layout overflow) -->
    <Transition name="app-fade">
      <div v-if="!isInitializing && !isUpdating" class="app-shell min-h-screen">
        <div class="app-content">
          <ErrorBoundary>
            <RouterView v-slot="{ Component }">
              <Transition name="page-fade" mode="out-in">
                <component :is="Component" />
              </Transition>
            </RouterView>
          </ErrorBoundary>
          <BaseToast />
          <UnsavedChangesShell />
          <MergeConflictDialog />
          <AiCompliancePolicyModal />
          <AiQuotaNotice />
        </div>
        <AuthOverlay :is-visible="authStore.isLoggingOut" mode="logout" />
        <AuthOverlay :is-visible="showLoginOverlay" mode="login" />
        <ActionOverlay />
        <CpAddonUnlockCelebrationOverlay v-if="!isInitializing && !isUpdating" />
        <CpAddonPackageTour
          v-if="!isInitializing && !isUpdating && sidebarTourCode"
          :key="sidebarTourKey"
          :addon-code="sidebarTourCode"
          :auto-start="true"
          @tour-ended="sidebarTourCode = null"
        />
        <div class="pointer-events-none fixed bottom-4 right-4 z-[9000] w-[min(22rem,calc(100vw-2rem))]">
          <div class="pointer-events-auto">
            <AppUpdateCard />
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { RouterView } from 'vue-router'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/auth'
import { useThemeStore } from '@/stores/theme'
import { useAppUpdate } from '@/composables/useAppUpdate'
import { useTenantBrandingDisplay } from '@/composables/useTenantBrandingDisplay'
import { useCpAddonToursStore } from '@/stores/cp-addon-tours'
import ErrorBoundary from '@/components/error/ErrorBoundary.vue'
import BaseToast from '@/components/base/BaseToast.vue'
import AuthOverlay from '@/components/auth/AuthOverlay.vue'
import ActionOverlay from '@/components/layout/ActionOverlay.vue'
import AppUpdateCard from '@/components/base/AppUpdateCard.vue'
import CpAddonUnlockCelebrationOverlay from '@/components/layout/CpAddonUnlockCelebrationOverlay.vue'
import CpAddonPackageTour from '@/components/layout/CpAddonPackageTour.vue'
import UnsavedChangesShell from '@/components/unsaved/UnsavedChangesShell.vue'
import MergeConflictDialog from '@/components/merge/MergeConflictDialog.vue'
import AiQuotaNotice from '@/components/assist/AiQuotaNotice.vue'
import AiCompliancePolicyModal from '@/components/assist/AiCompliancePolicyModal.vue'

const authStore = useAuthStore()
const themeStore = useThemeStore()
const appUpdate = useAppUpdate()
const { t } = useI18n()
const { displayName, logoUrl, logoAlt, headingStyle } = useTenantBrandingDisplay()
const showLoginOverlay = computed(() => authStore.isLoggingIn && !authStore.isLoggingOut)
const isInitializing = ref(true)
const isUpdating = computed(() => appUpdate.isUpdating.value)
const updatePhase = computed(() => appUpdate.updatePhase.value)
const updateProgress = computed(() => appUpdate.updateProgress.value)
const updatePhaseText = computed(() => t(`update.flow.${updatePhase.value}.title`))
const updatePhaseSubtext = computed(() => t(`update.flow.${updatePhase.value}.subtext`))

/** Active add-on code for the global sidebar-triggered tour (separate from the celebration overlay tour). */
const sidebarTourCode = ref<string | null>(null)
/**
 * Incremented on every restart request so the :key always changes,
 * forcing a remount of CpAddonPackageTour even when the code stays the same.
 */
const sidebarTourKey = ref(0)

const toursStore = useCpAddonToursStore()
const { restartSignals } = storeToRefs(toursStore)

watch(restartSignals, (signals) => {
  const entries = Object.entries(signals)
  if (entries.length === 0) return
  const latest = entries.reduce((a, b) => (b[1] > a[1] ? b : a))
  sidebarTourCode.value = latest[0]
  // Increment key to force remount even if the code is unchanged (e.g. repeated sidebar clicks).
  sidebarTourKey.value += 1
}, { deep: true })

onMounted(async () => {
  themeStore.applyTheme()
  await new Promise((resolve) => setTimeout(resolve, 400))
  isInitializing.value = false
})
</script>

<style scoped>
.app-container {
  position: relative;
  min-height: 100vh;
}

.app-shell {
  position: relative;
}

.app-content {
  min-height: 100vh;
}

.loader-fade-leave-active {
  transition: opacity 0.5s ease-out;
}

.loader-fade-leave-to {
  opacity: 0;
}

.app-fade-enter-active {
  transition: opacity 0.5s ease-in;
  transition-delay: 0.2s;
}

.app-fade-enter-from {
  opacity: 0;
}

.app-fade-enter-to {
  opacity: 1;
}

.page-fade-enter-active,
.page-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.page-fade-enter-from {
  opacity: 0;
  transform: translateY(6px);
}

.page-fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.update-fade-enter-active {
  transition: opacity 0.3s ease-in;
}

.update-fade-enter-from {
  opacity: 0;
}

.update-fade-enter-to {
  opacity: 1;
}

.phase-fade-enter-active,
.phase-fade-leave-active {
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
}

.phase-fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.phase-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
